<template>
	<LazyLoadWrapper>
		<div
			v-if="featuredProducts?.Items?.length"
			v-editable="blok"
			class="flex flex-col w-full main-wrapper !px-0"
		>
			<span
				class="text-3xl mb-4"
				:class="arrayToString(blok.title_styles) || 'font-heldane text-[55px]'"
				>{{ blok.title }}</span
			>
			<div>
				<StoryblokComponent
					v-for="carousel in blok.carousel"
					:key="carousel._uid"
					:blok="carousel"
					:slides="
						(carousel.component || '').toLowerCase() !== 'carousel'
							? null
							: featuredProducts?.Items
					"
					:type-of-slide="
						(carousel.component || '').toLowerCase() !== 'carousel'
							? null
							: ItemType.PRODUCT
					"
					:all-parametrics="allParametrics"
					:show-variants="showColorSwatches"
				/>
			</div>
		</div>
	</LazyLoadWrapper>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { FilterType } from "~/types/storyblok/productsPopulation";
import type { BlokFeaturedProducts } from "~/types/storyblok/storyblok";
import { ItemType, activeStatusID } from "~/utils/constants";
import LazyLoadWrapper from "~/components/LazyLoadWrapper.vue";
import { arrayToString } from "~/utils/arrayToString";

interface Props {
	blok: BlokFeaturedProducts;
}

const { blok } = defineProps<Props>();

const { listProductsByIds, listProductByFilters } = useNorcePwa();
const { fetchAllParametrics } = useAllParametrics();
const { localeProperties } = useI18n();
const { storeContext } = storeToRefs(useNorceStore());

// `show_color_swatches`: full pipeline — group variants + fetch the parametric
// catalog so cards render colour swatches. OFF by default (it enlarges the
// response: `returnAllVariants` pulls every sibling variant + parametric lookups).
const showColorSwatches = blok.show_color_swatches === true;
// `show_variants`: only sends `asVariants: true` so variant rows show as
// separate items in the carousel — no grouping, swatches or extra card info.
const showVariants = blok.show_variants === true;

const { data: featuredProducts } = await useAsyncData(
	`featured-products-${blok._uid}`,
	async () => {
		const populationConfig = blok.population_config?.[0];
		if (
			populationConfig?.id &&
			Object.values(FilterType).includes(populationConfig.entity)
		) {
			// Same per-block opt-in as the by-ids path below. The filter endpoint
			// groups variants server-side (no `returnAllVariants` — that flag is
			// specific to listProductsByIds3).
			return await listProductByFilters({
				[populationConfig.entity]: populationConfig.id,
				pageSize: populationConfig.pageSize,
				...storeContext.value,
				cultureCode: localeProperties.value.iso,
				// `show_variants` alone → just return variants as items.
				...(showColorSwatches || showVariants ? { asVariants: true } : {}),
				...(showColorSwatches
					? {
							statusSeed: String(activeStatusID),
							processColorVariants: true,
							// See by-ids note below: `true` would drop hand-picked /
							// population products that have an empty color-visibility seed.
							filterByAllowedColors: false
						}
					: {})
			});
		}
		const idSeed = blok.selected_products
			.map((productId) => productId)
			.join(",");
		// Variant flags mirror the category listing. Sent only when the block
		// opts in — otherwise Norce returns just the selected products (flat,
		// small) with no grouping or parametric work server-side.
		return await listProductsByIds({
			idSeed,
			...storeContext.value,
			cultureCode: localeProperties.value.iso,
			// `show_variants` alone → just return variants as items.
			...(showColorSwatches || showVariants ? { asVariants: true } : {}),
			...(showColorSwatches
				? {
						statusSeed: String(activeStatusID),
						returnAllVariants: "true",
						processColorVariants: true,
						// `false`: show every colour variant. `true` keeps only colours
						// in each product's ParametricMultipleSeed (the category-level
						// "color-visibility" seed), which is empty for hand-picked /
						// population products — so `true` would drop them entirely.
						filterByAllowedColors: false
					}
				: {})
		});
	}
);

// Swatch colours resolve from the parametric catalog on the client — only
// loaded when variants are shown.
const { data: allParametrics } = await useAsyncData(
	`featured-parametrics-${blok._uid}`,
	() => (showColorSwatches ? fetchAllParametrics() : Promise.resolve([]))
);
</script>
